import { useApps } from '@auth/useApps'
import { Account, AccountTypeEnum, getDelegatorPoints } from '@kudos/http-client'
import { AccountPermission, AppPermission, hasPermission } from '@shared/permissions'
import { useQuery } from '@tanstack/react-query'

export type UseCanSendPointsOptions = {
  delegatorId?: string
}

export type UseCanSendPointsResult = {
  canSendPoints: boolean
  delegatorAccounts: Account[] | undefined
  isDelegatorPointsFetched: boolean
}

export function useCanSendPoints(options: UseCanSendPointsOptions = {}): UseCanSendPointsResult {
  const { delegatorId } = options
  const [hasAppPermission] = useApps()

  const { data: delegatorAccounts, isFetched: isDelegatorPointsFetched } = useQuery({
    queryKey: ['delegatorPoints', delegatorId],
    queryFn: () => getDelegatorPoints(delegatorId ?? ''),
    enabled: !!delegatorId,
  })

  const delegatorAllocationAccount = delegatorAccounts?.find(
    account => account.type === AccountTypeEnum.ALLOCATION,
  )

  const canSendDelegatedPoints =
    delegatorAllocationAccount &&
    hasPermission(delegatorAllocationAccount, AccountPermission.SendDelegatedPoints)

  // Both pointsApp permissions describe the requesting user, so neither can gate a delegated send:
  // sendPoints tracks the user's own allocation, and viewPointsConfiguration is an admin-only
  // configuration gate that non-admin delegates never hold. account:sendDelegatedPoints on the
  // delegator's allocation account is the delegator-scoped signal, and the backend revokes it when
  // that account is locked.
  const canSendPoints = delegatorId
    ? Boolean(canSendDelegatedPoints)
    : hasAppPermission('pointsApp', AppPermission.PointsAppSendPoints)

  return {
    canSendPoints,
    delegatorAccounts,
    isDelegatorPointsFetched,
  }
}

export default useCanSendPoints
